/**
 * AddDemandResponseDialog component renders a modal dialog for scheduling a power curtail operation.
 * It includes a header with a title and a close button, and a form for selecting the scheduling options.
 *
 * @component
 * @example
 * return (
 *   <AddDemandResponseDialog />
 * )
 *
 * @returns {JSX.Element} The rendered AddDemandResponseDialog component.
 */
import React, {
  useEffect,
} from "react";
import {
  StyledIconButton,
  PrimaryButton,
  SecondaryButton,
  StyledToggleButton,
  StyledToggleButtonGroup,
  StyledRadio,
  StyledFormControlLabel,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import { useGetCurtailData } from "../../queries/useGetCurtailData.ts";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CheckIcon from "@mui/icons-material/Check";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog.js";
import {
  H6HeadlineSmall,
  H8TitleMedium,
  } from "../../../components/StyledComponents/Typography/Typography.tsx";
import {

  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  RadioGroup,
  Box,
  FormHelperText,
  CircularProgress,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import useDemandResponseContext from "../../context/useDemandResponseContext.js";
import { Controller, useForm } from "react-hook-form";
import {
  addDays,
  eachDayOfInterval,
  format,
  getDay,
  isSameDay,
  parseISO,
} from "date-fns";
import dayjs from "dayjs";
import {
  StyledTextField,
  StyledPaper,
  StyledFormControl,
  StyledSelect,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import { usePowerCurtail } from "../../queries/usePostConfig.ts";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { v4 as uuidv4 } from "uuid";
import * as constants from "../../../constants.js";

import { StyledMenuItem } from "../../../components/StyledComponents/Menu/Menu.tsx";

const AddDemandConfigModalHeader = (props) => {
  const { authToken } = props;
  const {
    setOpenPowerCurtailOperationModal,
    editModeOn,
    setEditModeOn,
    eventModalData,
    setEventModalData,
    setUseFamilyPowerMax,
    setUseFamilyPowerMin,
  } = useDemandResponseContext();
  const { formReset } = props;
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1rem 0"}
    >
      <H6HeadlineSmall>
        {editModeOn
          ? `Edit ${eventModalData?.id}`
          : "Add Power Curtail Operation"}
      </H6HeadlineSmall>
      <StyledIconButton colors={colors}
        onClick={() => {
          setOpenPowerCurtailOperationModal(false);
          setEditModeOn(false);
          setEventModalData([]);
          formReset();
          setUseFamilyPowerMax(null);
          setUseFamilyPowerMin(null);
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const  AddDemandConfigModal = (props) => {
  const { refetch } = props;
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const maxCalEventsInADay = constants.MAX_SCHEDULE_EVENTS;
  // const maxCalEventsInADay = 5;

  const {
    openPowerCurtailOperationModal,
    setOpenPowerCurtailOperationModal,
    setCalendarEvents,
    calendarEvents,
    eventModalData,
    editModeOn,
    siteLists,
    useFamilyPowerMin,
    useFamilyPowerMax,
    siteSelectedName,
    setUseFamilyPowerMin,
    setUseFamilyPowerMax,
    setSiteSelectedName,
  } = useDemandResponseContext();

  const defaultStartTime = dayjs().add(1, "minute");
  const defaultEndTime = dayjs().add(4, "minute");
  const {
    watch,
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    clearErrors,
    formState: { errors, isValid: isFormValid },
    getValues,
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      powerCurtailOperationSchedule: "schedule",
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      power: null,
      rampUpDownTime: null,
      siteConfig: "",
      scheduleOperations: "noRepeat",
      selectedDays: [], // Default to current day
      endsOn: dayjs().add(1, "month"), // Default to  date 3 month later
      powerOperationScope: "per_miner",
    },
  });

  // Reset form values when eventModalData changes

  const watchStartTime = watch("startTime");
  const watchEndTime = watch("endTime");
  const selectedOption = watch("siteConfig");
  const watchPowerOperationScope = watch("powerOperationScope");
  const isPerMinerScope =
    watchPowerOperationScope === "per_miner";
  const watchPowerCurtailOperationSchedule = watch(
    "powerCurtailOperationSchedule"
  );
  const scheduleOperations = watch("scheduleOperations");
  const selectedDays = watch("selectedDays");

  useEffect(() => {
    if (eventModalData) {
      reset({
        powerCurtailOperationSchedule:
          eventModalData.powerCurtailOperationSchedule ||
          "schedule",
        startTime: eventModalData.start
          ? dayjs(eventModalData.start)
          : defaultStartTime,
        endTime: eventModalData.end
          ? dayjs(eventModalData.end)
          : defaultEndTime,
        endsOn: eventModalData.endsOn
          ? eventModalData.endsOn
          : dayjs().add(1, "month"),
        scheduleOperations: "noRepeat",
        power: !isNaN(parseFloat(eventModalData.power))
          ? parseFloat(eventModalData.power)
          : null,
        rampUpDownTime: !isNaN(
          parseFloat(eventModalData.power)
        )
          ? parseInt(eventModalData.rampUpDownTime)
          : null,
      });
    }
  }, [eventModalData]);

  useEffect(() => {
    // Only update endsOn when user changes recurrence, not on full form reset
    const base = watchStartTime || dayjs();
    setValue(
      "endsOn",
      scheduleOperations === "daily"
        ? base.add(1, "week")
        : base.add(1, "month")
    );
  }, [scheduleOperations, watchStartTime]);
  const {
    data: curtailData,
    isLoading: isCurtailDataLoading,
    isFetching: isCurtailDataFetching,
    isFetched: isCurtailDataFetched,
    isRefetching: isCurtailDataRefetching,
    refetch: refetchCurtailData,
  } = useGetCurtailData({
    authToken: props.authToken,
    siteName: selectedOption,
  });
  let calEvents = [];
  const timeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  let deviceCount = "";

  // Per-miner power limits (will be calculated below)
  let perMinerMaxPower = 0;
  let perMinerMinPower = 0;

  if (curtailData) {
    if (curtailData?.status === "ok") {
      calEvents =
        curtailData &&
        curtailData?.curtailSchedule &&
        curtailData?.curtailSchedule.map((event) => ({
          ...event,
          id: event.id,
          source: event.source,
          start: toZonedTime(
            dayjs(event.start_time).toDate(),
            timeZone
          ),
          end: toZonedTime(
            dayjs(event.end_time).toDate(),
            timeZone
          ),
          startDay: dayjs(event.start_time).format(
            "DD/MM/YYYY"
          ),
          endDay: dayjs(event.end_time).format(
            "DD/MM/YYYY"
          ),
          color:
            event?.source === "manual"
              ? "#2D55C9"
              : event?.source.toLowerCase() === "voltus"
                ? "#7857FF"
                : event?.source.toLowerCase() === "cpower"
                  ? "#3D9DF5"
                  : "#3174ad",
        }));

      let pMax = 0;
      let pMin = 0;

      if (
        curtailData && 
        curtailData.hasOwnProperty("modelCount") && watchPowerOperationScope === "per_site"
      ) {
        // setModelCount(curtailData.modelCount);
        pMax = 0;
        pMin = 0;
        for (const [key, value] of Object.entries(
          curtailData.modelCount
        )) {
          if (
            constants.PRODUCT_FAMILY.includes(key) &&
            constants.PRODUCT_FAMILY_MAXPOWER[key] &&
            value > 0
          ) {
            // Calculate total site power (per-site mode)
            pMax +=
              value *
              constants.PRODUCT_FAMILY_MAXPOWER[key];
            pMin += value * constants.MINPOWER;


          }
        }
      }
      // Set per-site power limits (in MW)
      if (pMax > 0) {
        pMax = pMax / 1000000;
        setUseFamilyPowerMax(Number(pMax.toFixed(4)));
      } else {
        pMax = constants.AT1500_MAXPOWER / 1000000;
        setUseFamilyPowerMax(Number(pMax.toFixed(4)));
      }
      if (pMin > 0) {
        pMin = pMin / 1000000;
        setUseFamilyPowerMin(Number(pMin.toFixed(4)));
      } else {
        pMin = constants.MINPOWER / 1000000;
        setUseFamilyPowerMin(Number(pMin.toFixed(4)));
      }

      // Set per-miner power limits (in Watts)
      perMinerMinPower = constants.MINPOWER;
      if (
        curtailData &&
        curtailData.hasOwnProperty("modelCount") && watchPowerOperationScope === "per_miner"
      ) {
        for (const [key, value] of Object.entries(
          curtailData.modelCount
        )) {
          if (
            constants.PRODUCT_FAMILY.includes(key) &&
            constants.PRODUCT_FAMILY_MAXPOWER[key] &&
            value > 0
          ) {
            perMinerMaxPower = Math.max(
              perMinerMaxPower,
              constants.PRODUCT_FAMILY_MAXPOWER[key]
            );
          }
        }
      }
      // Fallback to AT1500_MAXPOWER if no models found
      if (perMinerMaxPower === 0) {
        perMinerMaxPower = constants.AT1500_MAXPOWER;
      }
      // Set state variables for per-miner power limits (in Watts, no conversion)
      setUseFamilyPowerMin(perMinerMinPower);
      setUseFamilyPowerMax(perMinerMaxPower);

      if (curtailData.totalDevices) {
        deviceCount = curtailData.totalDevices;
      }
    }
  }

  function generateUUID() {
    return uuidv4(); // Generates a UUID
  }

  function generateRandomString(length) {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * charactersLength)
      );
    }
    return result;
  }

  const {
    mutate: addPowerCurtailOperation,
    isPending,
    isSuccess,
  } = usePowerCurtail();

  function mapSelectedDaysToUTC(selectedDays, startTime) {
    const dayNameToNum = {
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6,
    };
    const numToDayName = [
      "sun",
      "mon",
      "tue",
      "wed",
      "thu",
      "fri",
      "sat",
    ];

    // Ensure startTime is a JS Date
    const jsStartTime =
      startTime instanceof Date
        ? startTime
        : startTime?.toDate?.() || new Date(startTime);
    if (isNaN(jsStartTime))
      return selectedDays.map(() => null);

    const localStartDayNum = getDay(jsStartTime);

    return selectedDays.map((localDay) => {
      const offset =
        (dayNameToNum[localDay] - localStartDayNum + 7) % 7;
      const localTargetDate = addDays(jsStartTime, offset);

      if (
        !(localTargetDate instanceof Date) ||
        isNaN(localTargetDate.getTime())
      )
        return null;

      const utcDayNum = localTargetDate.getUTCDay();
      return numToDayName[utcDayNum] ?? null;
    });
  }

  const onsubmit = (data) => {
    if (!editModeOn) {
      if (data.powerCurtailOperationSchedule === "now") {
        const finalData = {
          siteName: data.siteConfig,
          eventType: "Curtailment",
          operation: "now",
          per_miner_target_power:
            data.powerOperationScope === "per_miner",
          target_power_usage: !isNaN(parseFloat(data.power))
            ? parseFloat(data.power)
            : 0.0,
        };
        addPowerCurtailOperation(
          {
            ...finalData,
            operation_type: "manualPowerCurtailNow",
          },
          {
            onSuccess: async (response) => {
              if (response.status === "ok") {
                setToastMessage(
                  response.msg
                    ? `${response.msg}`
                    : "Curtail schedule created successfully. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
                );
                setToastOpen(true);
                await refetch();
                setOpenPowerCurtailOperationModal(false);
                reset();
                setUseFamilyPowerMax(null);
                setUseFamilyPowerMin(null);
              } else {
                setToastMessage("Error: " + response.msg);
                setToastOpen(true);
                setOpenPowerCurtailOperationModal(false);
              }
            },
            onError: (error) => {
              console.error("Error:", error);
              setToastMessage(
                "Error scheduling power curtail operation"
              );
              setToastOpen(true);
              setOpenPowerCurtailOperationModal(false);
            },
          }
        );
      }
      if (
        data.powerCurtailOperationSchedule === "schedule"
      ) {
        const finalData = {
          start_time: formatInTimeZone(
            data.startTime?.toDate(),
            "UTC",
            "yyyy-MM-dd'T'HH:mm:ss'Z'"
          ),
          end_time: formatInTimeZone(
            data.endTime?.toDate(),
            "UTC",
            "yyyy-MM-dd'T'HH:mm:ss'Z'"
          ),
          siteName: data.siteConfig,
          target_power_usage: !isNaN(parseFloat(data.power))
            ? parseFloat(data.power)
            : 0.0,
          eventType: "Curtailment",
          operation: "schedule",
          source: "manual",
          scheduleOP: "add",
          per_miner_target_power:
            data.powerOperationScope === "per_miner",
          rampup_time: !isNaN(parseInt(data.rampUpDownTime))
            ? parseInt(data.rampUpDownTime)
            : 0,
          id: generateUUID(),
          ...(data.scheduleOperations === "daily" ||
            data.scheduleOperations === "weekly"
            ? {
              recurring: {
                type: data.scheduleOperations,
                timezone:
                  Intl.DateTimeFormat().resolvedOptions()
                    .timeZone,
                timezoneOffsetMinutes:
                  new Date().getTimezoneOffset(),
                endson: data.endsOn
                  ? formatInTimeZone(
                    data.endsOn?.toDate(),
                    "UTC",
                    "yyyy-MM-dd'T'HH:mm:ss'Z'"
                  )
                  : "",
                ...(data.scheduleOperations ===
                  "weekly" &&
                  data.selectedDays &&
                  data.selectedDays.length > 0
                  ? {
                    days_of_week: data.selectedDays,
                  }
                  : {}),
              },
            }
            : {}),
        };

        addPowerCurtailOperation(
          {
            ...finalData,
            operation_type: "powerCurtailSchedule",
          },
          {
            onSuccess: async (response) => {
              if (response.status === "ok") {
                setToastMessage(
                  response.msg
                    ? ` ${response.msg}`
                    : "Curtail schedule created successfully. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
                );
                setToastOpen(true);
                setOpenPowerCurtailOperationModal(false);
                await refetch();
              } else {
                setToastMessage("Error: " + response.msg);
                setToastOpen(true);
                setOpenPowerCurtailOperationModal(false);
                reset();
                setUseFamilyPowerMax(null);
                setUseFamilyPowerMin(null);
              }
            },
            onError: (error) => {
              console.error("Error:", error);
              setToastMessage(
                "Error scheduling power curtail operation"
              );
              setToastOpen(true);
              setOpenPowerCurtailOperationModal(false);
            },
          }
        );
      }
      if (
        data.powerCurtailOperationSchedule === "standby"
      ) {
        const finalData = {
          start_time: formatInTimeZone(
            data.startTime?.toDate(),
            "UTC",
            "yyyy-MM-dd'T'HH:mm:ss'Z'"
          ),
          end_time: formatInTimeZone(
            data.endTime?.toDate(),
            "UTC",
            "yyyy-MM-dd'T'HH:mm:ss'Z'"
          ),
          siteName: data.siteConfig,
          target_power_usage: 0.00001,
          eventType: "Scheduled Standby",
          operation: "schedule",
          source: "manual",
          scheduleOP: "add",
          rampup_time: !isNaN(parseInt(data.rampUpDownTime))
            ? parseInt(data.rampUpDownTime)
            : 0,
          id: generateUUID(),
          ...(data.scheduleOperations === "daily" ||
            data.scheduleOperations === "weekly"
            ? {
              recurring: {
                type: data.scheduleOperations,
                timezone:
                  Intl.DateTimeFormat().resolvedOptions()
                    .timeZone,
                timezoneOffsetMinutes:
                  new Date().getTimezoneOffset(),
                endson: data.endsOn
                  ? formatInTimeZone(
                    data.endsOn?.toDate(),
                    "UTC",
                    "yyyy-MM-dd'T'HH:mm:ss'Z'"
                  )
                  : "",
                ...(data.scheduleOperations ===
                  "weekly" &&
                  data.selectedDays &&
                  data.selectedDays.length > 0
                  ? {
                    days_of_week: data.selectedDays,
                  }
                  : {}),
              },
            }
            : {}),
        };

        addPowerCurtailOperation(
          {
            ...finalData,
            operation_type: "powerCurtailSchedule",
          },
          {
            onSuccess: async (response) => {
              if (response.status === "ok") {
                setToastMessage(
                  response.msg
                    ? `${response.msg}`
                    : "Curtail schedule created successfully. Please check the Job Results page or check the jobs column for each miner in the miner inventory tab"
                );
                setToastOpen(true);
                setOpenPowerCurtailOperationModal(false);
                await refetch();
              } else {
                setToastMessage("Error: " + response.msg);
                setToastOpen(true);
                setOpenPowerCurtailOperationModal(false);
                reset();
                setUseFamilyPowerMax(null);
                setUseFamilyPowerMin(null);
              }
            },
            onError: (error) => {
              console.error("Error: ", error);
              setToastMessage(
                "Error scheduling power curtail operation"
              );
              setToastOpen(true);
              setOpenPowerCurtailOperationModal(false);
            },
          }
        );
      }
    } else {
      const calendarEventsCopy = [...calendarEvents];
      const index = calendarEventsCopy.findIndex(
        (calenderEvent) =>
          calenderEvent.id === eventModalData.id
      );
      const finalData =
        watchPowerCurtailOperationSchedule ===
        "schedule" && {
          source: "manual",
          id: generateRandomString(16),
          start: data.startTime?.toDate(),
          end: data.endTime?.toDate(),
          startDay: format(
            data.startTime.toDate(),
            "dd/MM/yyyy"
          ),
          endDay: format(
            data.endTime.toDate(),
            "dd/MM/yyyy"
          ),
          title: "random",
          color: "#2D55C9",
          per_miner_target_power:
            data.powerOperationScope === "per_miner",
          ...data,
        };
      calendarEventsCopy[index] = finalData;
      setCalendarEvents(calendarEventsCopy);
      setToastMessage("Updated successfully");
      setToastOpen(true);
      setOpenPowerCurtailOperationModal(false);
    }
  };

  function isOverlappingEvent(
    start,
    end,
    siteConfig,
    calEvents
  ) {
    if (watchPowerCurtailOperationSchedule === "now") {
      return false;
    }
    if (!Array.isArray(siteConfig) || !siteConfig)
      return false;

    const selectedSites = Array.isArray(siteConfig)
      ? siteConfig
      : [siteConfig];

    const sameSiteSelected =
      siteSelectedName === siteConfig;
    if (!sameSiteSelected) {
      return false;
    }

    const isOverlapping = calEvents.some((event) => {
      if (!event.start_time || !event.end_time) {
        return false;
      }

      const eventStart = dayjs(event.start);
      const eventEnd = dayjs(event.end);

      if (
        !eventStart.isValid() ||
        !eventEnd.isValid() ||
        !start.isValid() ||
        !end.isValid()
      ) {
        console.warn("Invalid dates in overlap check:", {
          eventStart,
          eventEnd,
          start,
          end,
        });
        return false;
      }

      const isOverlap =
        start.isBefore(eventEnd) && end.isAfter(eventStart);

      if (isOverlap) {
        console.log(
          "!!! Overlap detected with event:",
          event
        );
        return true;
      }

      return false;
    });
    if (isOverlapping) {
      console.log(
        "!!! Overlap detected with existing events for selected sites."
      );
    } else {
      console.log(" No overlap detected.");
    }
    return isOverlapping;
  }

  const countEventsOnDay = (date, calEvents) => {
    return (
      calEvents &&
      calEvents.filter((event) => {
        const eventStart = event.start_time
          ? parseISO(event.start_time)
          : event.start
            ? parseISO(event.start)
            : null;
        if (!eventStart) return false;
        return isSameDay(eventStart, date);
      }).length
    );
  };

  // Helper: check if any day in the range exceeds the max
  const exceedsMaxEventsInAnyDay = (
    start,
    end,
    calEvents,
    max
  ) => {
    if (watchPowerCurtailOperationSchedule === "now") {
      return false;
    }
    const days = eachDayOfInterval({ start, end });
    return days.some(
      (day) => countEventsOnDay(day, calEvents) >= max
    );
  };

  const { colors } = useTheme();

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={openPowerCurtailOperationModal}
      hideBackdrop
    >
      <AddDemandConfigModalHeader formReset={reset} />
      <form
        style={{ overflow: "auto" }}
        onSubmit={handleSubmit(onsubmit)}
      >
        {/* {errors.startTime && errors.endTime && (
          <Box
            sx={{
              backgroundColor: "#FDEDED",
              color: "#D8000C",
              padding: "1rem",
              borderRadius: "4px",
              margin: "1rem",
            }}
          >
            {errors.startTime.message} <br />
            {errors.endTime.message}
          </Box>
        )} */}
        <DialogContent sx={{ padding: 0 }}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            className="demand-config-modal-body"
            gap={4}
            maxHeight={"60vh"}
            overflow={"auto"}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              className="scheduling-decision-container"
              gap={4}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                background: colors.background.popover, // or your modal background color  
                // background: "#fff", // or your modal background color
                // paddingTop: "1rem",
                // paddingBottom: "1rem",
              }}
            >
              <H8TitleMedium>Schedule</H8TitleMedium>
              <Controller
                name="powerCurtailOperationSchedule"
                control={control}
                defaultValue={
                  watchPowerCurtailOperationSchedule
                }
                render={({ field }) => (
                  <StyledToggleButtonGroup
                    exclusive
                    {...field}
                    onChange={(e, value) => {
                      if (!value) return;
                      field.onChange(value);
                    }}
                    value={field.value}
                    colors={colors}
                  >
                    {/* <StyledToggleButton
                      sx={{
                        textTransform: "none",
                        color: "#444653",
                        minWidth: "6rem",
                      }}
                      value={"now"}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: "0.5rem",
                          justifyContent: "center",
                          alignItems: "center",
                          flexGrow: 1,
                        }}
                      >
                        {field.value === "now" && (
                          <CheckIcon
                            sx={{ marginBottom: "3%" }}
                            fontSize="small"
                          />
                        )}
                        <LabelLarge>Now</LabelLarge>
                      </Box>
                    </StyledToggleButton> */}
                    <StyledToggleButton
                      sx={{
                        textTransform: "none",
                        minWidth: "6rem",
                      }}
                      value={"schedule"}
                      colors={colors}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: "0.5rem",
                          justifyContent: "center",
                          alignItems: "center",
                          flexGrow: 1,
                        }}
                      >
                        {field.value === "schedule" && (
                          <CheckIcon
                            sx={{ marginBottom: "3%" }}
                            fontSize="small"
                          />
                        )}
                        Power
                      </Box>
                    </StyledToggleButton>
                    <StyledToggleButton
                      sx={{
                        textTransform: "none",
                        minWidth: "6rem",
                      }}
                      value={"standby"}
                      colors={colors}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: "0.5rem",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        {field.value === "standby" && (
                          <CheckIcon
                            sx={{ marginBottom: "3%" }}
                            fontSize="small"
                          />
                        )}
                          Standby
                      </Box>
                    </StyledToggleButton>
                  </StyledToggleButtonGroup>
                )}
              />
            </Box>
            {
              <Box
                display={"grid"}
                gridTemplateRows={"auto auto auto"}
                gap={7.76}
              >
                {(watchPowerCurtailOperationSchedule ===
                  "schedule" ||
                  watchPowerCurtailOperationSchedule ===
                  "standby") && (
                    <Box
                      display={"grid"}
                      gridTemplateColumns={
                        "2.0fr 2.0fr 0.7fr"
                      }
                      alignSelf={"center"}
                      gap={4}
                    >
                      <Controller
                        name="startTime"
                        control={control}
                        rules={{
                          required: "Start time is required",
                          validate: (value) => {
                            const start = dayjs(value);
                            if (!start.isValid()) {
                              return "Invalid date/time";
                            }
                            const rawEnd = getValues("endTime");
                            const end = rawEnd ? dayjs(rawEnd) : null;
                            if (end?.isValid() && start.isAfter(end)) {
                              return "Start time should be before end time";
                            }
                            if (start.isBefore(dayjs())) {
                              return "Start time should be in the future";
                            }
                            if (start.isBefore(dayjs().add(1, "minute"))) {
                              return "Start time should be at least 1 minutes from now";
                            }
                            if (start.isAfter(dayjs().add(2, "month"))) {
                              return "Start time should not be more than 2 months in the future";
                            }
                            const watchedEndRaw =
                              watchEndTime?.toDate?.() ?? watchEndTime;
                            const endForRange = end?.isValid()
                              ? end
                              : watchedEndRaw
                                ? dayjs(watchedEndRaw)
                                : null;
                            if (
                              endForRange &&
                              exceedsMaxEventsInAnyDay(
                                start.toDate(),
                                endForRange.toDate(),
                                calEvents,
                                maxCalEventsInADay
                              )
                            ) {
                              return `You can schedule only ${maxCalEventsInADay} events per day in the selected range.`;
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <StyledFormControl colors={colors} >
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                            >
                              <DateTimePicker
                                label="Start Time"
                                value={field.value}
                                disablePast
                                maxDate={dayjs().add(2, "month")}
                                slots={{
                                  desktopPaper: StyledPaper,
                                }}
                                onChange={(value) => {
                                  field.onChange(value);
                                  setValue(
                                    "endTime",
                                    dayjs(value).add(
                                      4,
                                      "minute"
                                    )
                                  );
                                  trigger("startTime");
                                  trigger("endTime");
                                }}
                                slotProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiPaper-root": {
                                        backgroundColor: colors?.background?.paper,
                                      },
                                    },
                                  },
                                  desktopPaper: {
                                    sx: {
                                      backgroundColor: colors?.background?.paper,
                                      borderRadius: "12px",
                                      boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
                                
                                      "& .MuiPickersCalendarHeader-label": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersCalendarHeader-switchViewIcon": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiDayCalendar-weekDayLabel": {
                                        color: colors?.text?.secondary,
                                      },
                                
                                      "& .MuiPickersDay-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersDay-root.MuiPickersDay-today": {
                                        border: `1px solid ${colors?.text?.muted}`,
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        borderRadius: "1rem",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersLayout-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                
                                      "& .MuiPickersToolbar-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiDialogActions-root button": {
                                        color: colors?.primary?.main,
                                        fontWeight: 600,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                    },
                                  },
                                  textField: {
                                    error: Boolean(errors.startTime),
                                    helperText: errors.startTime
                                      ? errors.startTime.message
                                      : "MM/DD/YYYY at HH:MM am/pm",
                                
                                    sx: {
                                      "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                        color: colors?.text?.muted,
                                      },
                                      "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                        color: colors?.text?.muted, // date/time value
                                      },
                                      "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                        color: colors?.text?.muted, // icons / AM-PM adornments
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                      "& .MuiFormHelperText-root": {
                                        color: colors?.text?.muted,
                                      },
                                    },
                                    inputProps: {
                                      style: { color: colors?.text?.primary },
                                      placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                    },
                                  },
                                  day: {
                                    sx: {
                                      "&.MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&.Mui-disabled": {
                                        WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                      },
                                    },
                                  },
                                  digitalClockSectionItem: {
                                    sx: {
                                      "&.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        borderRadius: "50%",
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&:hover": {
                                        backgroundColor: colors.hover?.muted,
                                      },
                                    },
                                  },
                                }}
                                
                                renderInput={(props) => (
                                  <StyledTextField colors={colors}
                                    {...props}
                                    variant="outlined"
                                    error={Boolean(
                                      errors.startTime
                                    )}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </StyledFormControl>
                        )}
                      />
                      <Controller
                        name="endTime"
                        control={control}
                        rules={{
                          required: "End time is required",
                          validate: (value) => {
                            if (!value) return true;
                            const end = dayjs(value);
                            if (!end.isValid()) {
                              return "Invalid date/time";
                            }
                            const rawStart = getValues("startTime");
                            const start = rawStart ? dayjs(rawStart) : null;
                            if (start?.isValid() && end.isBefore(start)) {
                              return "End time should be after start time";
                            }
                            if (end.isBefore(dayjs())) {
                              return "End time should be in the future";
                            }
                            if (
                              start?.isValid() &&
                              end.isBefore(start.add(4, "minute"))
                            ) {
                              return "End time should be at least 4 minutes from start time";
                            }
                            const schedulingOp = getValues(
                              "scheduleOperations"
                            );
                            if (
                              (schedulingOp === "weekly" ||
                                schedulingOp === "daily") &&
                              start?.isValid() &&
                              end.diff(start, "hour", true) > 12
                            ) {
                              return "End time should not be more than 12 hours after start time for events";
                            }
                            // if (
                            //   dayjs(value).diff(
                            //     dayjs(watchStartTime),
                            //     "hour",
                            //     true
                            //   ) > 24
                            // ) {
                            //   return "End time should not be more than 24 hours after start time";
                            // }
                            const watchedStartRaw =
                              watchStartTime?.toDate?.() ?? watchStartTime;
                            const startForRange = start?.isValid()
                              ? start
                              : watchedStartRaw
                                ? dayjs(watchedStartRaw)
                                : null;
                            const exceedsDailyCap =
                              startForRange &&
                              exceedsMaxEventsInAnyDay(
                                startForRange.toDate(),
                                end.toDate(),
                                calEvents,
                                maxCalEventsInADay
                              );
                            if (exceedsDailyCap) {
                              return `You can schedule only ${maxCalEventsInADay} events per day in the selected range.`;
                            }

                            // if (
                            //   isOverlappingEvent(
                            //     dayjs(watchStartTime), // start
                            //     dayjs(value), // end
                            //     selectedOption,
                            //     calEvents
                            //   )
                            // ) {
                            //   return "Selected time overlaps with an existing event for this site.";
                            // }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <StyledFormControl colors={colors}  >
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                            >
                              <DateTimePicker
                                label="End Time"
                                // defaultValue={defaultEndTime}
                                value={field.value}
                                // maxDate={
                                //   scheduleOperations ===
                                //     "daily" ||
                                //   (scheduleOperations ===
                                //     "weekly" &&
                                //     watchStartTime)
                                //     ? watchStartTime.add(
                                //         12,
                                //         "hour"
                                //       )
                                //     : null
                                // }
                                maxDateTime={
                                  (scheduleOperations ===
                                    "daily" ||
                                    scheduleOperations ===
                                    "weekly") &&
                                    watchStartTime
                                    ? watchStartTime.add(
                                      12,
                                      "hour"
                                    )
                                    : null
                                }
                                disablePast
                                slots={{
                                  desktopPaper: StyledPaper,
                                }}
                                onChange={(date) => {
                                  field.onChange(date);
                                  trigger("startTime");
                                  trigger("endTime");
                                }}
                                slotProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiPaper-root": {
                                        backgroundColor: colors?.background?.paper,
                                      },
                                    },
                                  },
                                  desktopPaper: {
                                    sx: {
                                      backgroundColor: colors?.background?.paper,
                                      borderRadius: "12px",
                                      boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
                                
                                      "& .MuiPickersCalendarHeader-label": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersCalendarHeader-switchViewIcon": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiDayCalendar-weekDayLabel": {
                                        color: colors?.text?.secondary,
                                      },
                                
                                      "& .MuiPickersDay-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersDay-root.MuiPickersDay-today": {
                                        border: `1px solid ${colors?.text?.muted}`,
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        borderRadius: "1rem",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersLayout-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                
                                      "& .MuiPickersToolbar-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiDialogActions-root button": {
                                        color: colors?.primary?.main,
                                        fontWeight: 600,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                    },
                                  },
                                  textField: {
                                    error: Boolean(errors.endTime),
                                    helperText: errors.endTime
                                      ? errors.endTime.message
                                      : "MM/DD/YYYY at HH:MM am/pm",
                                
                                    sx: {
                                      "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                        color: colors?.text?.muted,
                                      },
                                      "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                        color: colors?.text?.muted, // date/time value
                                      },
                                      "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                        color: colors?.text?.muted, // icons / AM-PM adornments
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                      "& .MuiFormHelperText-root": {
                                        color: colors?.text?.muted,
                                      },
                                    },
                                    inputProps: {
                                      style: { color: colors?.text?.primary },
                                      placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                    },
                                  },
                                  day: {
                                    sx: {
                                      "&.MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&.Mui-disabled": {
                                        WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                      },
                                    },
                                  },
                                  digitalClockSectionItem: {
                                    sx: {
                                      "&.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        borderRadius: "50%",
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&:hover": {
                                        backgroundColor: colors.hover?.muted,
                                      },
                                    },
                                  },
                                }}
                                
                                renderInput={(props) => (
                                  <StyledTextField colors={colors}
                                    {...props}
                                    variant="outlined"
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </StyledFormControl>
                        )}
                      />
                      <Box
                        display={"grid"}
                        alignItems={"center"}
                      >
                        <SecondaryButton colors={colors} 
                        
                          onClick={async () => {
                            const newStart = dayjs().add(
                              2,
                              "minute"
                            );
                            await setValue(
                              "startTime",
                              newStart,
                              {
                                shouldValidate: true,
                              }
                            );
                            await setValue(
                              "endTime",
                              newStart.add(4, "minute"),
                              {
                                shouldValidate: true,
                              }
                            );
                            console.log("errors", errors);
                            // trigger(["startTime", "endTime"]);
                            clearErrors([
                              "startTime",
                              "endTime",
                            ]);
                          }}
                        >
                          Reset time
                        </SecondaryButton>
                      </Box>
                    </Box>
                  )}
                <Box
                  display={"grid"}
                  gridTemplateColumns={"auto"}
                  gap={4}
                >
                  <Controller
                    name="scheduleOperations"
                    control={control}
                    defaultValue={scheduleOperations}
                    render={({ field }) => (
                      <StyledFormControl
                        fullWidth
                        margin="none"
                        colors={colors}
                      >
                        <InputLabel>Recurrence</InputLabel>
                        <StyledSelect
                          {...field}
                          label="Recurrence"
                          value={
                            field.value ||
                            watchPowerCurtailOperationSchedule
                          }
                          colors={colors}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            trigger("startTime");
                            trigger("endTime");
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                backgroundColor: colors.background.paper,
                                color: colors.text.primary,
                              },
                            },
                          }}
                        >
                          <StyledMenuItem colors={colors} value="noRepeat">
                            Does not repeat
                          </StyledMenuItem>
                          <StyledMenuItem colors={colors} value="daily">
                            Daily
                          </StyledMenuItem>
                          <StyledMenuItem colors={colors} value="weekly">
                            Weekly
                          </StyledMenuItem>
                        </StyledSelect>
                      </StyledFormControl>
                    )}
                  />
                </Box>
                {scheduleOperations === "weekly" && (
                  <Box display={"grid"} gap={8}>
                    <Box display={"grid"} gap={4}>
                      <H8TitleMedium>
                        Repeat on
                      </H8TitleMedium>
                      <Box
                        display="grid"
                        gridTemplateColumns={
                          "repeat(7, 1fr)"
                        }
                        gap={2}
                      >
                        <Controller
                          name="selectedDays"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (
                                !value ||
                                value.length === 0
                              ) {
                                return "At least one day must be selected";
                              }
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <Box
                              display="grid"
                              gridTemplateColumns="repeat(7, 1fr)"
                              gap={2}
                            >
                              {[
                                "sun",
                                "mon",
                                "tue",
                                "wed",
                                "thu",
                                "fri",
                                "sat",
                              ].map((day) => (
                                <StyledToggleButton
                                  colors={colors}
                                  key={day}
                                  value={day}
                                  selected={
                                    Array.isArray(
                                      selectedDays
                                    )
                                      ? selectedDays.includes(
                                        day
                                      )
                                      : false
                                  }
                                  onClick={() => {
                                    const current =
                                      field.value || [];
                                    field.onChange(
                                      current.includes(day)
                                        ? current.filter(
                                          (d) => d !== day
                                        )
                                        : [...current, day]
                                    );
                                  }}
                                >
                                  {Array.isArray(
                                    selectedDays
                                  ) &&
                                    selectedDays.includes(
                                      day
                                    ) ? (
                                    <CheckIcon />
                                  ) : null}
                                  {day
                                    .charAt(0)
                                    .toUpperCase() +
                                    day.slice(1)}
                                </StyledToggleButton>
                              ))}

                              <FormHelperText
                                error={Boolean(
                                  errors.selectedDays
                                )}
                                sx={{
                                  gridColumn: "span 7",
                                  color: colors?.text?.muted, 
                                }}
                              >
                                {errors.selectedDays
                                  ? errors.selectedDays
                                    .message
                                  : "Select at least one day"}
                              </FormHelperText>
                            </Box>
                          )}
                        />
                      </Box>
                    </Box>
                    <Box display={"grid"} gap={4}>
                      <Controller
                        name="endsOn"
                        control={control}
                        rules={{
                          required: "endsOn is required",
                          validate: (value) => {
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <StyledFormControl colors={colors}  margin="none">
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                            >
                              <DatePicker
                                label="Ends on"
                                value={field.value}
                                shouldDisableDate={(date) =>
                                  date.isBefore(
                                    dayjs().startOf("day")
                                  )
                                }
                                maxDate={
                                  watchStartTime
                                    ? watchStartTime
                                      .add(2, "month")
                                      .endOf("day")
                                    : dayjs()
                                      .add(2, "month")
                                      .endOf("day")
                                }
                                slotProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiPaper-root": {
                                        backgroundColor: colors?.background?.paper,
                                      },
                                    },
                                  },
                                  desktopPaper: {
                                    sx: {
                                      backgroundColor: colors?.background?.paper,
                                      borderRadius: "12px",
                                      boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
                                
                                      "& .MuiPickersCalendarHeader-label": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersCalendarHeader-switchViewIcon": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiDayCalendar-weekDayLabel": {
                                        color: colors?.text?.secondary,
                                      },
                                
                                      "& .MuiPickersDay-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersDay-root.MuiPickersDay-today": {
                                        border: `1px solid ${colors?.text?.muted}`,
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        borderRadius: "1rem",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersLayout-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                
                                      "& .MuiPickersToolbar-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiDialogActions-root button": {
                                        color: colors?.primary?.main,
                                        fontWeight: 600,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                    },
                                  },
                                  textField: {
                                    error: Boolean(errors.endTime),
                                    helperText: errors.endTime
                                      ? errors.endTime.message
                                      : "MM/DD/YYYY at HH:MM am/pm",
                                
                                    sx: {
                                      "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                        color: colors?.text?.muted,
                                      },
                                      "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                        color: colors?.text?.muted, // date/time value
                                      },
                                      "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                        color: colors?.text?.muted, // icons / AM-PM adornments
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                      "& .MuiFormHelperText-root": {
                                        color: colors?.text?.muted,
                                      },
                                    },
                                    inputProps: {
                                      style: { color: colors?.text?.primary },
                                      placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                    },
                                  },
                                  day: {
                                    sx: {
                                      "&.MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&.Mui-disabled": {
                                        WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                      },
                                    },
                                  },
                                  digitalClockSectionItem: {
                                    sx: {
                                      "&.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        borderRadius: "50%",
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&:hover": {
                                        backgroundColor: colors.hover?.muted,
                                      },
                                    },
                                  },
                                }}
                                onChange={(value) => {
                                  field.onChange(value);
                                  trigger("endsOn");
                                }}
                                renderInput={(props) => (
                                  <StyledTextField colors={colors}
                                    {...props}
                                    variant="outlined"
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </StyledFormControl>
                        )}
                      />
                    </Box>
                  </Box>
                )}
                {scheduleOperations === "daily" && (
                  <Box display={"grid"} gap={8}>
                    <Box display={"grid"} gap={4}>
                      <Controller
                        name="endsOn"
                        control={control}
                        rules={{
                          required: "endsOn is required",
                          validate: (value) => {
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <StyledFormControl colors={colors}  margin="none">
                            <LocalizationProvider
                              dateAdapter={AdapterDayjs}
                            >
                              <DatePicker
                                label="Ends on"
                                value={field.value}
                                shouldDisableDate={(date) =>
                                  date.isBefore(
                                    dayjs().startOf("day")
                                  )
                                }
                                maxDate={
                                  watchStartTime
                                    ? watchStartTime
                                      .add(2, "month")
                                      .endOf("day")
                                    : dayjs()
                                      .add(2, "month")
                                      .endOf("day")
                                }
                                slots={{
                                  desktopPaper: StyledPaper,
                                }}
                                onChange={(value) => {
                                  field.onChange(value);
                                  trigger("endsOn");
                                }}
                                slotProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiPaper-root": {
                                        backgroundColor: colors?.background?.paper,
                                      },
                                    },
                                  },
                                  desktopPaper: {
                                    sx: {
                                      backgroundColor: colors?.background?.paper,
                                      borderRadius: "12px",
                                      boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
                                
                                      "& .MuiPickersCalendarHeader-label": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersCalendarHeader-switchViewIcon": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                                        color: colors?.primary?.main,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                
                                      "& .MuiDayCalendar-weekDayLabel": {
                                        color: colors?.text?.secondary,
                                      },
                                
                                      "& .MuiPickersDay-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersDay-root.MuiPickersDay-today": {
                                        border: `1px solid ${colors?.text?.muted}`,
                                      },
                                
                                      "& .MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        borderRadius: "1rem",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.muted,
                                        },
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-disabled": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root .MuiPickersDigitalClock-root": {
                                        color: colors?.text?.primary,
                                      },
                                
                                      "& .MuiPickersLayout-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiPickersLayout-root": {
                                        color: colors?.text?.muted,
                                      },
                                
                                      "& .MuiPickersToolbar-root .MuiButtonBase-root": {
                                        color: colors?.text?.primary,
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                
                                      "& .MuiPickersToolbar-root .Mui-selected": {
                                        backgroundColor: colors?.primary?.main,
                                        color: "#fff",
                                        "&:hover": {
                                          backgroundColor: colors?.hover?.primary,
                                        },
                                      },
                                
                                      "& .MuiDialogActions-root button": {
                                        color: colors?.primary?.main,
                                        fontWeight: 600,
                                        "&:hover": { color: colors?.primary?.dark },
                                      },
                                    },
                                  },
                                  textField: {
                                    error: Boolean(errors.endTime),
                                    helperText: errors.endTime
                                      ? errors.endTime.message
                                      : "MM/DD/YYYY at HH:MM am/pm",
                                
                                    sx: {
                                      "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
                                        color: colors?.text?.muted,
                                      },
                                      "& .MuiOutlinedInput-root .MuiOutlinedInput-input": {
                                        color: colors?.text?.muted, // date/time value
                                      },
                                      "& .MuiOutlinedInput-root .MuiInputAdornment-root, & .MuiSvgIcon-root": {
                                        color: colors?.text?.muted, // icons / AM-PM adornments
                                        "&:hover": { color: colors?.primary?.main },
                                      },
                                      "& .MuiFormHelperText-root": {
                                        color: colors?.text?.muted,
                                      },
                                    },
                                    inputProps: {
                                      style: { color: colors?.text?.primary },
                                      placeholder: "MM/DD/YYYY at HH:MM am/pm",
                                    },
                                  },
                                  day: {
                                    sx: {
                                      "&.MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&.Mui-disabled": {
                                        WebkitTextFillColor: `${colors?.text?.hint} !important`,
                                      },
                                    },
                                  },
                                  digitalClockSectionItem: {
                                    sx: {
                                      "&.Mui-selected": {
                                        backgroundColor: colors.primary.main,
                                        borderRadius: "50%",
                                        "&:hover": {
                                          backgroundColor: colors.hover?.primary,
                                        },
                                      },
                                      "&:hover": {
                                        backgroundColor: colors.hover?.muted,
                                      },
                                    },
                                  },
                                }}
                                renderInput={(props) => (
                                  <StyledTextField colors={colors}
                                    {...props}
                                    variant="outlined"
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          </StyledFormControl>
                        )}
                      />
                    </Box>
                  </Box>
                )}
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={4}
                  className="sitesContainer"
                >
                  <H8TitleMedium>
                    Select Site to schedule this event for
                  </H8TitleMedium>
                  <Box
                    display="flex"
                    flexDirection="row"
                    flexGrow={1}
                  >
                    <StyledFormControl
                      colors={colors}
                      fullWidth
                      margin="none"
                    >
                      <InputLabel>Site</InputLabel>
                      {/* <InputLabel htmlFor="">Status</InputLabel> */}
                      <Controller
                        name="siteConfig"
                        control={control}
                        defaultValue={siteSelectedName}
                        rules={{
                          validate: (value) => {
                            if (
                              !value ||
                              value.length === 0
                            )
                              return "Site is required";
                            // if (
                            //   isOverlappingEvent(
                            //     dayjs(watchStartTime),
                            //     dayjs(watchEndTime),
                            //     value,
                            //     calEvents
                            //   )
                            // ) {
                            //   return "Selected time overlaps with an existing event for this site.";
                            // }

                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <StyledSelect
                            {...field}
                            label="Site"
                            colors={colors}
                            value={field.value || ""}
                            style={{ flex: 1 }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                  backgroundColor: colors.background.paper,
                                  color: colors.text.primary,
                                },
                              },
                            }}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value
                              );
                              setSiteSelectedName(
                                e.target.value
                              );

                              // if (
                              //   e.target.value &&
                              //   isOverlappingEvent(
                              //     dayjs(watchStartTime),
                              //     dayjs(watchEndTime),
                              //     e.target.value,
                              //     calEvents
                              //   )
                              // ) {
                              //   trigger("siteConfig");
                              //   trigger("startTime");
                              //   trigger("endTime");
                              //   setValue("power", null);
                              //   setValue(
                              //     "rampUpDownTime",
                              //     null
                              //   );
                              // }
                            }}
                            onBlur={() => {
                              trigger("siteConfig");
                              trigger("startTime");
                              trigger("endTime");
                            }}
                          >
                            {siteLists?.map(
                              (siteName, index) => (
                                <StyledMenuItem colors={colors}
                                  key={index}
                                  value={siteName}
                                >
                                  {siteName}
                                </StyledMenuItem>
                              )
                            )}
                          </StyledSelect>
                        )}
                      />
                    </StyledFormControl>
                </Box>
              </Box>
                {watchPowerCurtailOperationSchedule ===
                  "schedule" && (
                    <Box
                      display={"grid"}
                      gridTemplateRows={"auto auto"}
                      gap={3}
                    >
                      <H8TitleMedium>
                        Operation Type
                      </H8TitleMedium>
                      <Controller
                        name="powerOperationScope"
                        control={control}
                        defaultValue="per_miner"
                        rules={{
                          required:
                            "Select an operation scope",
                        }}
                        render={({ field }) => (
                          <FormControl
                            component="fieldset"
                            error={Boolean(
                              errors.powerOperationScope
                            )}
                          >
                            <RadioGroup
                              {...field}
                              value={field.value || "per_site"}
                              onChange={(event) => {
                                field.onChange(
                                  event.target.value || ""
                                );
          
                              }}
                            >
                              <StyledFormControlLabel
                                colors={colors}
                                value="per_miner"
                                control={
                                  <StyledRadio colors={colors} />
                                }
                                label="Per Miner"
                              />
                              <StyledFormControlLabel
                                colors={colors}
                                value="per_site"
                                control={
                                  <StyledRadio colors={colors} />
                                }
                                label="Per Site"
                              />
                            </RadioGroup>
                            {errors.powerOperationScope && (
                              <FormHelperText>
                                {
                                  errors
                                    .powerOperationScope
                                    .message
                                }
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />
                    </Box>
                  )}
                {(watchPowerCurtailOperationSchedule ===
                  "now" ||
                  watchPowerCurtailOperationSchedule ===
                  "schedule") && (
                    <Box
                      display={"grid"}
                      gridTemplateRows={"auto auto"}
                      gap={4}
                    >
                      <H8TitleMedium>
                        Change Power
                      </H8TitleMedium>
                      <Controller
                        name="power"
                        control={control}
                        rules={{
                          required: "Power is required",
                      }}
                        render={({ field }) => (
                          <StyledTextField
                            colors={colors}
                            margin="none"
                            variant="outlined"
                            type="number"
                            label={isPerMinerScope ? "In W" : "In MW"}
                            {...field}
                            error={Boolean(errors.power)}
                            helperText={
                              errors.power
                                ? errors.power.message
                                : !!useFamilyPowerMin &&
                                  !!useFamilyPowerMax
                                  ? isPerMinerScope
                                    ? `Suggested power between ${perMinerMinPower} W and ${perMinerMaxPower} W, miner count: ${deviceCount ? deviceCount : 0}`
                                    : `Suggested MW value between  ${(parseFloat(useFamilyPowerMin) / 1000000).toFixed(4)} MW and  ${(parseFloat(useFamilyPowerMax) / 1000000).toFixed(4)} MW, miner count: ${deviceCount ? deviceCount : 0}`
                                  : selectedOption &&
                                    watchPowerOperationScope
                                    ? `Fetching power limits...`
                                    : `Select a site and operation type to see its power limits`
                            }
                          />
                        )}
                      />
                    </Box>
                  )}
                {(watchPowerCurtailOperationSchedule ===
                  "standby" ||
                  watchPowerCurtailOperationSchedule ===
                  "schedule") && (
                    <Box
                      display={"grid"}
                      gridTemplateRows={"auto auto"}
                      gap={4}
                    >
                      <H8TitleMedium>
                        Ramp Up/Down Time
                      </H8TitleMedium>
                      <Controller
                        name="rampUpDownTime"
                        control={control}
                        rules={{
                          required:
                            "Ramp up/down time is required",
                          min: {
                            value: 1,
                            message:
                              "Ramp up/down should be at least 1 second",
                          },
                          max: {
                            value: 900,
                            message:
                              "Ramp up/down should not exceed 900 seconds ",
                          },
                        }}
                        render={({ field }) => (
                          <StyledTextField colors={colors}
                            margin="none"
                            variant="outlined"
                            type="number"
                            label="In seconds"
                            {...field}
                            error={Boolean(
                              errors.rampUpDownTime
                            )}
                            helperText={
                              errors.rampUpDownTime
                                ? errors.rampUpDownTime
                                  .message
                                : "Enter a number between 1 and 900"
                            }
                          />
                        )}
                      />
                    </Box>
                  )}
              </Box>
            }
          </Box>
        </DialogContent>
        <DialogActions sx={{ paddingTop: "1rem" }}>
          <PrimaryButton colors={colors}
            disabled={!isFormValid || isPending}
            type="submit"
            endIcon={
              isPending && <CircularProgress size={24} sx={{ color: colors.primary.main }}/>
            }
          >
            {isPending ? "Scheduling..." : "Schedule"}
          </PrimaryButton>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

const AddDemandResponseDialog = (props) => {
  const { refetch, calEvents, authToken } = props;
  return (
    <>
      <AddDemandConfigModal
        refetch={refetch}
        calEvents={calEvents}
        authToken={authToken}
      />
    </>
  );
};

export default AddDemandResponseDialog;
